import { LogOut, User, Shield } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { EnhancedAvatar } from '@/components/ui/enhanced-avatar'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useLogout, useForceLogout } from '@/features/auth/hooks/useAuth'
import type { User as UserType } from '@/types/entities'

interface UserAvatarMenuProps {
    user: UserType
}

export function UserAvatarMenu({ user }: UserAvatarMenuProps) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const logout = useLogout()
    const forceLogout = useForceLogout()

    const handleLogout = () => {
        // Try normal logout first, but have force logout as backup
        logout.mutate()

        // If logout is still pending after 3 seconds, force logout
        setTimeout(() => {
            if (logout.isPending) {
                console.log('Logout taking too long, forcing logout')
                forceLogout()
            }
        }, 3000)
    }

    const handleProfile = () => {
        navigate('/profile-settings')
    }



    const getRoleBadge = (roles: UserType['roles']) => {
        if (!roles || !Array.isArray(roles)) {
            return <User className="h-3 w-3 text-gray-500" />
        }
        if (roles.some(role => role.name === 'admin')) {
            return <Shield className="h-3 w-3 text-red-500" />
        }
        if (roles.some(role => role.name === 'user')) {
            return <User className="h-3 w-3 text-blue-500" />
        }
        return <User className="h-3 w-3 text-gray-500" />
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <EnhancedAvatar
                        name={user.name}
                        avatarUrl={user.avatarUrl}
                        avatarColor={user.avatarColor}
                        userId={user.id}
                        size="sm"
                    />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">{user.name}</p>
                        <p className="text-xs leading-none text-muted-foreground">
                            {user.email}
                        </p>
                        <div className="flex items-center space-x-1 pt-1">
                            {getRoleBadge(user.roles)}
                            <span className="text-xs text-muted-foreground">
                                {user.roles && Array.isArray(user.roles) ? user.roles.map(role => role.name).join(', ') : 'No roles'}
                            </span>
                        </div>
                    </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleProfile}>
                    <User className="mr-2 h-4 w-4" />
                    <span>{t('profile.settings')}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} disabled={logout.isPending}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t('auth.logout')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={forceLogout} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t('auth.forceLogout')}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}






















